/**
 * The single, declaration-agnostic text-patch write path (v0.10).
 *
 * A structured model mutation becomes a surgical text edit: offset-based
 * {@link TextEdit}s spliced into the source in one left-to-right pass. Every
 * region NOT covered by an edit stays BYTE-IDENTICAL — comments, hand-formatting,
 * sibling declarations. This is the deliberate opposite of `format-source.ts`,
 * which re-indents the whole document.
 *
 * `applyTextEdits` + the {@link TextEdit} shape are the decl-agnostic core (moved
 * out of `process-patch.ts`, which now re-exports them). On top of that sit the
 * declaration-site synthesizers that the WYSIWYG / table / matrix write paths
 * compose:
 *   - {@link upsertStyleClauseEdit} / {@link upsertPinEdit} — view `style`/`pin`
 *     writes (P2a, WYSIWYG drag).
 *   - {@link setFieldCellEdit} — a table getter-cell write into a module field
 *     (P2b).
 *   - {@link scaffoldEdgeEdit} — a matrix empty-cell `A > B` scaffold (P4).
 *
 * Spans carry UTF-16 offsets directly (`SourceSpan.start.offset` /
 * `.end.offset`), so no line/col → offset conversion is needed; every edit is
 * computed straight from those offsets. `spanFrom` builds a decl/clause span
 * `[firstToken.start, lastToken.end)` where the last token of a braced body is
 * its closing `}` — so `span.end.offset - 1` is the `}` itself.
 */

import type { Body, InterfaceBody, ModuleDecl, ProcessDecl, ProcessStep, SourceSpan } from "./types.js";
import type { FlowClause, KnobBinding, StyleClause, StyleMember, ViewDeclV2 } from "./v010-ast.js";

/** The body shape the member helpers operate over — module `Body` and the
 *  narrower `InterfaceBody` both fit (interface bodies have no sub-decls). */
export type PatchableBody = InterfaceBody &
  Partial<Pick<Body, "rosters" | "surfaces" | "interfaces" | "modules" | "processes" | "subprocesses" | "views">>;

/** A replacement of `[start, end)` (UTF-16 offsets) with `newText`. */
export interface TextEdit {
  readonly start: number;
  readonly end: number;
  readonly newText: string;
}

/**
 * Apply non-overlapping {@link TextEdit}s to `text` in one pass. Untouched spans
 * are copied verbatim (byte-identical). Throws if two edits overlap.
 */
export function applyTextEdits(text: string, edits: readonly TextEdit[]): string {
  const sorted = [...edits].sort((a, b) => a.start - b.start || a.end - b.end);
  let out = "";
  let pos = 0;
  for (const e of sorted) {
    if (e.start > e.end) throw new Error(`inverted text edit [${e.start}, ${e.end})`);
    if (e.start < pos) throw new Error(`overlapping text edits at offset ${e.start} (prev ended ${pos})`);
    out += text.slice(pos, e.start) + e.newText;
    pos = e.end;
  }
  return out + text.slice(pos);
}

// =============================================================================
// Declaration-site synthesizers
// =============================================================================
//
// Each returns `TextEdit[]` (composed by `applyTextEdits`) and keeps every
// unrelated byte identical: an edit either replaces exactly the span of the one
// thing it changes, or INSERTS at a zero-width anchor (`start === end`) placed
// after the last body member — the closing `}` and everything else is never in
// the edit range. Insertion text carries its own indentation, derived from the
// existing body's bytes (`indentBefore`), so no surrounding line is disturbed.

const INDENT = "    ";

/** The already-rendered text of one style member (`pin -120, 340`, `rename "X"`,
 *  `color: red`). Placed verbatim inside the clause braces; the caller owns the
 *  member syntax — the synthesizer stays decl-agnostic about it. */
export type StyleMemberText = string;

/**
 * `upsertStyleClauseEdit` — insert a `style <selectorText> { <member> }` clause
 * into a view body, OR extend an existing clause whose subject is byte-equal to
 * `selectorText` with `<member>`.
 *
 * Extending is **same-directive replacement**: one member per directive
 * identity per subject. A repeated gesture (the board's rename fired twice on
 * the same node, a re-colored node) REPLACES the prior member's span instead of
 * appending a sibling — the projection renders repeated members last-wins, so
 * accumulation (`rename "A"  rename "B"`) looks right while the text grows one
 * dead member per gesture. `pin` already replaced via {@link upsertPinEdit};
 * this generalizes the rule to every directive kind (see
 * {@link memberIdentityOf} for what "same directive" means per kind).
 *
 * A new clause is anchored after the last existing body member (never over the
 * closing `}`), matching the body's indentation. Extending anchors after the
 * clause's last member. The subject match compares the RAW source bytes of the
 * existing subject against `selectorText`, so equality is literal.
 */
export function upsertStyleClauseEdit(
  src: string,
  view: ViewDeclV2,
  selectorText: string,
  member: StyleMemberText,
): TextEdit[] {
  const existing = findStyleClauseBySelector(src, view, selectorText);
  const bodyIndent = viewBodyIndent(src, view);
  if (existing) {
    const prior = findMemberByIdentity(existing, member);
    if (prior?.source) {
      return [{ start: prior.source.start.offset, end: prior.source.end.offset, newText: member }];
    }
    return [appendMemberEdit(src, existing, bodyIndent, member)];
  }

  const clauseBlock = renderStyleClause(selectorText, member, bodyIndent);
  return [insertBodyMemberEdit(src, viewBrace(src, view), viewLastMemberEnd(view), bodyIndent, clauseBlock)];
}

/**
 * `upsertPinEdit` — the WYSIWYG drag write: upsert `style <targetName> { pin <x>,
 * <y> }` with SIGNED literals. If a `pin` directive already exists under that
 * selector, replace exactly its coordinate span; else extend the clause (or add
 * a fresh one, via {@link upsertStyleClauseEdit}).
 */
export function upsertPinEdit(
  src: string,
  view: ViewDeclV2,
  targetName: string,
  x: number,
  y: number,
): TextEdit[] {
  const member = `pin ${num(x)}, ${num(y)}`;
  const clause = findStyleClauseBySelector(src, view, targetName);
  if (!clause) return upsertStyleClauseEdit(src, view, targetName, member);

  const pin = clause.members.find((m) => m.kind === "pin");
  if (pin?.source) return [{ start: pin.source.start.offset, end: pin.source.end.offset, newText: member }];
  return [appendMemberEdit(src, clause, viewBodyIndent(src, view), member)];
}

/** One instance knob binding: the knob name + an already-rendered RHS literal
 *  (bare number/ident, or quoted string — the caller owns the value syntax, so
 *  the synthesizer stays value-kind-agnostic, mirroring {@link StyleMemberText}). */
export interface ViewInstanceBinding {
  readonly name: string;
  readonly value: string;
}

/**
 * `upsertViewInstance` — the S16 knob-persist write (a view INSTANCE). Two
 * modes, chosen by whether `view` is ITSELF an instance:
 *
 *  - `view` is a PARENT (no `instanceOf`): synthesize a fresh instance
 *    `view <view.name> <instanceName> { <name>: <value> … }` appended at the
 *    file's TOP LEVEL (end of `src`) — the same root-append `scaffoldViewEdit`
 *    uses. The stable-id slot after `view` is left for the formatter to mint.
 *  - `view` is an INSTANCE (`instanceOf` set): update its bindings in place —
 *    one op per binding, replacing an existing binding's value span (anchored
 *    on its `:` so the name stays byte-identical) or inserting a fresh
 *    `<name>: <value>` line into the header zone (bindings precede clauses).
 *    `instanceName` is ignored in this mode (the instance already has a name).
 */
export function upsertViewInstance(
  src: string,
  view: ViewDeclV2,
  instanceName: string,
  bindings: readonly ViewInstanceBinding[],
): TextEdit[] {
  if (!view.instanceOf) {
    const inner = bindings.map((b) => `${INDENT}${b.name}: ${b.value}`).join("\n");
    const block = `view ${view.name} ${instanceName} {\n${inner}\n}`;
    const sep = src.endsWith("\n") ? "\n" : "\n\n";
    return [{ start: src.length, end: src.length, newText: `${sep}${block}\n` }];
  }

  const existing = new Map<string, KnobBinding>();
  for (const b of view.bindings ?? []) existing.set(b.name, b);
  // Fresh bindings land after the last existing binding (header zone); with no
  // bindings yet, the view-member insert falls to the after-`{` anchor, which
  // is still BEFORE the first clause.
  const anchor = maxEnd((view.bindings ?? []).map((b) => b.source));
  const edits: TextEdit[] = [];
  for (const b of bindings) {
    const prior = existing.get(b.name);
    const nameEnd = prior?.nameSource?.end.offset ?? prior?.source?.start.offset;
    if (prior?.source && nameEnd !== undefined) {
      const colon = src.indexOf(":", nameEnd);
      if (colon >= 0 && colon < prior.source.end.offset) {
        edits.push({ start: colon + 1, end: prior.source.end.offset, newText: ` ${b.value}` });
        continue;
      }
    }
    edits.push(insertViewMemberEdit(src, view, anchor, `${b.name}: ${b.value}`));
  }
  return edits;
}

/**
 * `setFieldCellEdit` — a table getter-cell write into the MODEL: set the field at
 * `path` on `element` to `value` (already-rendered RHS text). If the field
 * exists, replace exactly its value (path + `:` stay byte-identical, anchoring on
 * the separator colon so dotted paths survive); else insert a new
 * `<path>: <value>` field, anchored after the last field.
 */
export function setFieldCellEdit(
  src: string,
  // Structural: module AND interface decls fit (widened for the interface
  // value ops — ModuleDecl callers are unaffected).
  element: { readonly body: PatchableBody; readonly source?: SourceSpan },
  path: readonly string[],
  value: string,
): TextEdit[] {
  const target = element.body.fields.find((f) => sameName(f.path, path));
  if (target?.source) {
    const colon = src.indexOf(":", target.source.start.offset);
    if (colon >= 0 && colon < target.source.end.offset) {
      return [{ start: colon + 1, end: target.source.end.offset, newText: ` ${value}` }];
    }
  }

  const bodyIndent = elementBodyIndent(src, element, element.body.fields);
  const line = `${path.join(".")}: ${value}`;
  // Anchor after the last TOP-LEVEL field when fields exist (idiomatic
  // order; sub-body flattened fields excluded — inserting inside a field's
  // `{ … }` would reparse under its path), else the last member of ANY kind.
  const fieldSpans = topLevelSpans(
    element.body.fields.map((f) => f.source).filter((s): s is SourceSpan => !!s),
  );
  const lastEnd = maxEnd(fieldSpans) ?? bodyLastMemberEnd(element.body);
  return [insertBodyMemberEdit(src, elementBrace(src, element), lastEnd, bodyIndent, line)];
}

/**
 * `scaffoldEdgeEdit` — the matrix empty-cell scaffold: insert a `<fromName> >
 * <toName>` step into a process body, anchored after the last step and matching
 * step indentation.
 */
export function scaffoldEdgeEdit(
  src: string,
  process: ProcessDecl,
  fromName: string,
  toName: string,
): TextEdit[] {
  const line = `${fromName} > ${toName}`;
  const bodyIndent = stepBodyIndent(src, process);
  // Same anchoring rule as setFieldCellEdit: a step-less process may still hold
  // header fields/aspects — never take the brace-splice branch over them.
  const lastEnd =
    maxEnd(process.steps.map(stepSpan)) ??
    maxEnd([...(process.fields ?? []), ...(process.aspects ?? [])].map((f) => f.source));
  return [insertBodyMemberEdit(src, elementBrace(src, process), lastEnd, bodyIndent, line)];
}

// =============================================================================
// Body-member helpers (exported for the typed-op layer, model-mutations.ts)
// =============================================================================

/** Drop spans nested INSIDE another span of the set. The parser flattens
 *  field `{ … }` sub-bodies into `body.fields` with spans inside the parent
 *  field's braces — anchoring on those would land inserts INSIDE the
 *  sub-body (reparsing as `parent.child` paths, or not parsing at all). */
function topLevelSpans(spans: readonly SourceSpan[]): SourceSpan[] {
  return spans.filter(
    (s) =>
      !spans.some(
        (t) =>
          t !== s &&
          t.start.offset <= s.start.offset &&
          s.end.offset <= t.end.offset &&
          (t.start.offset < s.start.offset || s.end.offset < t.end.offset),
      ),
  );
}

/** Every span-bearing TOP-LEVEL member of a module/surface body. */
function bodyMemberSpans(body: PatchableBody): SourceSpan[] {
  const spans: (SourceSpan | undefined)[] = [
    ...body.fields.map((m) => m.source),
    ...body.aspects.map((m) => m.source),
    ...(body.rosters ?? []).map((m) => m.source),
    ...body.descriptions.map((m) => m.source),
    ...body.drops.map((m) => m.source),
    ...(body.surfaces ?? []).map((m) => m.source),
    ...(body.interfaces ?? []).map((m) => m.source),
    ...(body.modules ?? []).map((m) => m.source),
    ...(body.processes ?? []).map((m) => m.source),
    ...(body.subprocesses ?? []).map((m) => m.source),
    ...(body.views ?? []).map((m) => m.source),
  ];
  return topLevelSpans(spans.filter((s): s is SourceSpan => !!s));
}

/**
 * Anchor bundle for VIEW body writes (the ordered-clause decl): the brace
 * pair, member indent, the header zone's last end (fields + knobs — header
 * members must stay BEFORE the first clause), the last clause end, and every
 * clause's span for index-addressed ops.
 */
export function viewMemberAnchors(
  src: string,
  view: ViewDeclV2,
): {
  brace: { open: number; close: number };
  bodyIndent: string;
  lastHeaderEnd: number | null;
  lastClauseEnd: number | null;
  clauseSpans: readonly (SourceSpan | undefined)[];
} {
  return {
    brace: viewBrace(src, view),
    bodyIndent: viewBodyIndent(src, view),
    lastHeaderEnd: maxEnd([
      ...view.header.fields.map((f) => f.source),
      ...view.header.knobs.map((k) => k.source),
    ]),
    lastClauseEnd: maxEnd(view.clauses.map((c) => c.source)),
    clauseSpans: view.clauses.map((c) => c.source),
  };
}

/** Insert `block` as a view body member at the given anchor (after `at`,
 *  else the content-preserving empty/occupied-body branch). */
export function insertViewMemberEdit(
  src: string,
  view: ViewDeclV2,
  at: number | null,
  block: string,
): TextEdit {
  const a = viewMemberAnchors(src, view);
  return insertBodyMemberEdit(src, a.brace, at, a.bodyIndent, block);
}

/**
 * Anchor bundle for a `flow … bpmn { lane/pool }` BODY (S-lane-edit): the brace
 * pair inside the flow clause, the member indent, the last clause end, and each
 * body clause's span for index-addressed ops. Mirrors {@link viewMemberAnchors}
 * scoped one level deeper (the flow clause is itself a view clause). The caller
 * guarantees `flow.source` and `flow.body` are present (a `bpmn` flow with a
 * brace body — the only editable case).
 */
export function flowBodyAnchors(
  src: string,
  flow: FlowClause,
): {
  brace: { open: number; close: number };
  bodyIndent: string;
  lastClauseEnd: number | null;
  clauseSpans: readonly (SourceSpan | undefined)[];
} {
  const span = flow.source!;
  const brace = braceOf(src, span, span.start.offset);
  const body = flow.body ?? [];
  const firstStart = body.find((c) => c.source)?.source?.start.offset ?? null;
  const bodyIndent =
    firstStart !== null ? indentBefore(src, firstStart) : indentBefore(src, span.start.offset) + INDENT;
  return {
    brace,
    bodyIndent,
    lastClauseEnd: maxEnd(body.map((c) => c.source)),
    clauseSpans: body.map((c) => c.source),
  };
}

/** Insert `block` as a flow-body member at the flow clause's body end. */
export function insertFlowMemberEdit(src: string, flow: FlowClause, block: string): TextEdit {
  const a = flowBodyAnchors(src, flow);
  return insertBodyMemberEdit(src, a.brace, a.lastClauseEnd, a.bodyIndent, block);
}

/** Byte offset just past the LAST member of any kind, or null for empty. */
export function bodyLastMemberEnd(body: PatchableBody): number | null {
  return maxEnd(bodyMemberSpans(body));
}

/** Byte offset of the FIRST member of any kind, or null for empty. */
export function bodyFirstMemberStart(body: PatchableBody): number | null {
  let m: number | null = null;
  for (const s of bodyMemberSpans(body)) {
    m = m === null ? s.start.offset : Math.min(m, s.start.offset);
  }
  return m;
}

/**
 * Insert `block` as a new body member of `decl`, anchored after the last
 * member of ANY kind (empty body → brace splice). The generic sibling of the
 * field-anchored insert inside {@link setFieldCellEdit}.
 */
export function insertBodyMember(
  src: string,
  decl: { source?: SourceSpan; body: PatchableBody },
  block: string,
): TextEdit[] {
  // Bodyless decl: there is no `{` inside the span — the brace scan would
  // grab a LATER decl's brace and emit an inverted (or corrupting) edit.
  // Materialize a body carrying the block instead (§4a, one fragment).
  const brace = elementBrace(src, decl);
  const end = decl.source?.end.offset ?? src.length;
  if (brace.open < 0 || brace.open >= end) {
    return materializeBodyEdit(src, decl, block.split("\n"));
  }
  const first = bodyFirstMemberStart(decl.body);
  const bodyIndent =
    first !== null ? indentBefore(src, first) : indentBefore(src, decl.source?.start.offset ?? 0) + INDENT;
  return [insertBodyMemberEdit(src, brace, bodyLastMemberEnd(decl.body), bodyIndent, block)];
}

/**
 * Insert `block` as the FIRST body member (descriptions convention). Falls
 * back to the empty-body splice when there are no members.
 */
export function prependBodyMember(
  src: string,
  decl: { source?: SourceSpan; body: PatchableBody },
  block: string,
): TextEdit[] {
  const first = bodyFirstMemberStart(decl.body);
  if (first === null) return insertBodyMember(src, decl, block);
  const brace = elementBrace(src, decl);
  if (brace.open >= 0 && brace.open < first && !src.slice(brace.open + 1, first).includes("\n")) {
    // One-liner body (`service X { version: v1 }`): the first member shares
    // the header line, so a line-start insert would land BEFORE the decl.
    // Go inline instead, `;`-separated (§4.9 members are ;/newline-separated).
    return [{ start: brace.open + 1, end: brace.open + 1, newText: ` ${block};` }];
  }
  const indent = indentBefore(src, first);
  const lineStart = src.lastIndexOf("\n", first - 1) + 1;
  return [{ start: lineStart, end: lineStart, newText: `${indent}${block}\n` }];
}

/**
 * Materialize a body on a bodyless decl (§4a): ONE combined fragment
 * ` {\n …members… \n}` appended at the decl's end — never a two-stage apply.
 */
export function materializeBodyEdit(
  src: string,
  decl: { source?: SourceSpan },
  memberLines: readonly string[],
): TextEdit[] {
  const end = decl.source?.end.offset ?? src.length;
  const outerIndent = indentBefore(src, decl.source?.start.offset ?? 0);
  const inner = memberLines.map((l) => `${outerIndent}${INDENT}${l}`).join("\n");
  return [{ start: end, end, newText: ` {\n${inner}\n${outerIndent}}` }];
}

/**
 * Remove the member at `[start, end)` separator-aware: consumes one adjacent
 * `;` separator when the member shares its line, the whole line range when it
 * owns it, and falls back to the bare span otherwise. Never touches sibling
 * members (§2 invariants — v0.9 `;` bodies and one-liners are idiomatic).
 */
export function removeMemberEdits(src: string, start: number, end: number): TextEdit[] {
  // Trailing `; ` → inline removal, keep the line for the sibling after us.
  let e = end;
  while (src[e] === " " || src[e] === "\t") e++;
  if (src[e] === ";") {
    e++;
    while (src[e] === " " || src[e] === "\t") e++;
    return [{ start, end: e, newText: "" }];
  }
  // Leading `;` → we are the last inline sibling; consume the separator.
  let s = start;
  while (s > 0 && (src[s - 1] === " " || src[s - 1] === "\t")) s--;
  if (s > 0 && src[s - 1] === ";") {
    return [{ start: s - 1, end, newText: "" }];
  }
  // Whole-line(s) removal when the span owns its line range.
  const lineStart = src.lastIndexOf("\n", start - 1) + 1;
  const nl = src.indexOf("\n", end);
  const before = src.slice(lineStart, start);
  const after = nl === -1 ? src.slice(end) : src.slice(end, nl);
  // Trailing `\r` counts as line-end whitespace (CRLF sources).
  if (/^[ \t]*$/.test(before) && /^[ \t]*\r?$/.test(after)) {
    return [{ start: lineStart, end: nl === -1 ? src.length : nl + 1, newText: "" }];
  }
  return [{ start, end, newText: "" }];
}

// =============================================================================
// Internal helpers (byte-level, span-driven)
// =============================================================================

/** The leading whitespace of the line containing `offset` (its indentation). */
export function indentBefore(src: string, offset: number): string {
  const lineStart = src.lastIndexOf("\n", offset - 1) + 1;
  let i = lineStart;
  while (i < offset && (src[i] === " " || src[i] === "\t")) i++;
  return src.slice(lineStart, i);
}

/** Max `end.offset` over a set of (optional) spans, or `null` if none present. */
function maxEnd(spans: readonly (SourceSpan | undefined)[]): number | null {
  let m: number | null = null;
  for (const s of spans) if (s) m = m === null ? s.end.offset : Math.max(m, s.end.offset);
  return m;
}

/** A process step's span (`source` on simple/note steps, `span` on compound). */
function stepSpan(step: ProcessStep): SourceSpan | undefined {
  const s = step as { source?: SourceSpan; span?: SourceSpan };
  return s.source ?? s.span;
}

function sameName(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

function num(n: number): string {
  return String(n);
}

/** `{ open, close }` byte offsets of a braced-body decl: `open` = the `{`,
 *  `close` = the `}` (`span.end.offset - 1`). */
function braceOf(src: string, span: SourceSpan | undefined, scanFrom: number): { open: number; close: number } {
  const open = src.indexOf("{", scanFrom);
  const close = (span?.end.offset ?? src.length) - 1;
  return { open, close };
}

function viewBrace(src: string, view: ViewDeclV2): { open: number; close: number } {
  return braceOf(src, view.source, view.source?.start.offset ?? 0);
}

function elementBrace(src: string, decl: { source?: SourceSpan }): { open: number; close: number } {
  return braceOf(src, decl.source, decl.source?.start.offset ?? 0);
}

/**
 * Insert `block` as a body member. Prefer anchoring AFTER the last existing
 * member (`lastEnd`) — a zero-width insert that never touches the closing brace,
 * so every other byte is preserved. With no member anchor, the inter-brace
 * region is spliced ONLY when it is pure whitespace: anything else there
 * (comments, `on`-plane clauses, members the span index can't see) is content
 * a blind replace would DELETE — those bodies get a zero-width insert right
 * after the `{` instead.
 */
function insertBodyMemberEdit(
  src: string,
  brace: { open: number; close: number },
  lastEnd: number | null,
  bodyIndent: string,
  block: string,
): TextEdit {
  if (lastEnd !== null) {
    // The anchor may sit INSIDE an enclosing group whose members the parser
    // flattened (a field `{ … }` sub-body, an `aspect { … }` block): the last
    // member's span ends before the group's `}`. Consume any run of
    // whitespace + closing braces short of the BODY's own `}` so the insert
    // lands after the group, not inside it.
    let at = lastEnd;
    for (;;) {
      let j = at;
      while (j < brace.close && (src[j] === " " || src[j] === "\t" || src[j] === "\n" || src[j] === "\r")) j++;
      if (j < brace.close && src[j] === "}") {
        at = j + 1;
        continue;
      }
      break;
    }
    return { start: at, end: at, newText: `\n${bodyIndent}${block}` };
  }
  const outerIndent = bodyIndent.slice(0, Math.max(0, bodyIndent.length - INDENT.length));
  const between = src.slice(brace.open + 1, brace.close);
  if (/\S/.test(between)) {
    // Untracked content between the braces — preserve every byte of it.
    return { start: brace.open + 1, end: brace.open + 1, newText: `\n${bodyIndent}${block}` };
  }
  return { start: brace.open + 1, end: brace.close, newText: `\n${bodyIndent}${block}\n${outerIndent}` };
}

function renderStyleClause(selectorText: string, member: StyleMemberText, bodyIndent: string): string {
  return `style ${selectorText} {\n${bodyIndent}${INDENT}${member}\n${bodyIndent}}`;
}

/** Append `member` to an existing style clause, before its `}`. */
function appendMemberEdit(src: string, clause: StyleClause, bodyIndent: string, member: StyleMemberText): TextEdit {
  if (clause.members.length > 0) {
    const memberIndent = clause.members[0]!.source
      ? indentBefore(src, clause.members[0]!.source.start.offset)
      : bodyIndent + INDENT;
    const at = maxEnd(clause.members.map((m) => m.source));
    if (at !== null) return { start: at, end: at, newText: `\n${memberIndent}${member}` };
  }
  // Empty clause body: splice between its braces.
  const close = (clause.source?.end.offset ?? src.length) - 1;
  const open = src.indexOf("{", clause.source?.start.offset ?? 0);
  return { start: open + 1, end: close, newText: `\n${bodyIndent}${INDENT}${member}\n${bodyIndent}` };
}

/**
 * The directive identity of a RENDERED style member — what makes a second write
 * REPLACE it rather than append a sibling. `rename` / `pin` are singular per
 * subject; a field rewrite (`color: red`) or group (`widget { … }`) keys on its
 * path; `use` keys on its bundle (different bundles compose, the same bundle is
 * an idempotent re-apply). Unknown shapes return null — append, never guess a
 * replacement. The member text is caller-rendered, so identity is derived from
 * its leading bytes (the same vocabulary the parser tokenizes).
 */
function memberIdentityOf(member: StyleMemberText): { kind: string; key?: string } | null {
  const t = member.trim();
  const head = /^([^\s:{]+)/.exec(t)?.[1];
  if (head === "rename") return { kind: "rename" };
  if (head === "pin") return { kind: "pin" };
  if (head === "use") {
    const bundle = /^use\s+([^\s;]+)/.exec(t)?.[1];
    return bundle ? { kind: "use", key: bundle } : null;
  }
  if (head === undefined) return null;
  const rest = t.slice(head.length);
  if (/^\s*:/.test(rest)) return { kind: "rewrite", key: head };
  if (/^\s*\{/.test(rest)) return { kind: "group", key: head };
  return null;
}

/** The existing clause member the new `member` text REPLACES (same directive
 *  identity, span-bearing), or undefined → append. */
function findMemberByIdentity(clause: StyleClause, member: StyleMemberText): StyleMember | undefined {
  const id = memberIdentityOf(member);
  if (!id) return undefined;
  return clause.members.find((m) => {
    if (!m.source) return false;
    switch (id.kind) {
      case "rename":
        return m.kind === "rename";
      case "pin":
        return m.kind === "pin";
      case "use":
        return m.kind === "use" && m.bundle === id.key;
      case "rewrite":
        return m.kind === "rewrite" && m.path.join(".") === id.key;
      case "group":
        return m.kind === "group" && m.path.join(".") === id.key;
      default:
        return false;
    }
  });
}

function findStyleClauseBySelector(src: string, view: ViewDeclV2, selectorText: string): StyleClause | undefined {
  for (const c of view.clauses) {
    if (c.kind !== "style") continue;
    const s = c.subject.source;
    if (!s) continue;
    if (src.slice(s.start.offset, s.end.offset) === selectorText) return c;
  }
  return undefined;
}

/** Byte offset just past the last body member of a view, or `null` if empty. */
function viewLastMemberEnd(view: ViewDeclV2): number | null {
  return maxEnd([
    ...view.clauses.map((c) => c.source),
    ...view.header.fields.map((f) => f.source),
    ...view.header.knobs.map((k) => k.source),
  ]);
}

function viewBodyIndent(src: string, view: ViewDeclV2): string {
  const first =
    view.clauses[0]?.source ?? view.header.fields[0]?.source ?? view.header.knobs[0]?.source;
  if (first) return indentBefore(src, first.start.offset);
  return indentBefore(src, view.source?.start.offset ?? 0) + INDENT;
}

function elementBodyIndent(src: string, element: { source?: SourceSpan }, fields: readonly { source?: SourceSpan }[]): string {
  if (fields[0]?.source) return indentBefore(src, fields[0].source.start.offset);
  return indentBefore(src, element.source?.start.offset ?? 0) + INDENT;
}

function stepBodyIndent(src: string, process: ProcessDecl): string {
  const first = process.steps.map(stepSpan).find((s) => s);
  if (first) return indentBefore(src, first.start.offset);
  return indentBefore(src, process.source?.start.offset ?? 0) + INDENT;
}
